import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const VARIANTS = {
  success: {
    icon: CheckCircle2,
    iconClass: 'text-sage-600',
    barClass: 'bg-sage-600',
  },
  error: {
    icon: AlertCircle,
    iconClass: 'text-red-600',
    barClass: 'bg-red-600',
  },
  info: {
    icon: Info,
    iconClass: 'text-earth-700',
    barClass: 'bg-earth-700',
  },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((message, variant = 'success', duration = 3500) => {
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, variant }]);
    if (duration > 0) {
      setTimeout(() => remove(id), duration);
    }
  }, [remove]);

  const api = useMemo(() => ({
    success: (msg, duration) => push(msg, 'success', duration),
    error: (msg, duration) => push(msg, 'error', duration),
    info: (msg, duration) => push(msg, 'info', duration),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed top-6 right-6 z-[200] flex flex-col gap-3 pointer-events-none">
        <AnimatePresence>
          {toasts.map((t) => {
            const v = VARIANTS[t.variant] || VARIANTS.info;
            const Icon = v.icon;
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, x: 40, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 40, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 280, damping: 24 }}
                className="pointer-events-auto relative flex items-start gap-3 bg-white rounded-2xl shadow-xl border border-sage-100 pl-5 pr-4 py-4 min-w-[280px] max-w-sm overflow-hidden"
              >
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${v.barClass}`} />
                <Icon className={`flex-shrink-0 mt-0.5 ${v.iconClass}`} size={20} />
                <p className="flex-1 text-sm text-earth-900 leading-snug">{t.message}</p>
                <button
                  onClick={() => remove(t.id)}
                  className="text-sage-400 hover:text-sage-700 transition-colors"
                  aria-label="Zatvori"
                >
                  <X size={16} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
